import { Subscription } from 'rxjs';
import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';

import { ApiCommissionService } from './../../../services/api/api-commission.service';
import { ActionEnum } from './../../../models/generic';
import { DataService } from 'src/app/services/data.service';
import { ApiDialogService } from './../../../services/api/api-cdialog.service';
import {
  IBankCommission,
  ICommissionInfo,
  IFilterModalCommission,
  IParametersCommission,
  IProductCommission,
  ISearchFilterCommission
} from './../../../models/commission';
import { IFilterContracts } from 'src/app/models/filters';
import { IHeaderItem, IPagination } from 'src/app/models/generic';
import { IDialogCommissionData } from 'src/app/models/dialog';

@Component({
  selector: 'app-comissao-list',
  templateUrl: './comissao-list.component.html',
  styleUrls: ['./comissao-list.component.scss']
})
export class ComissaoListComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('commissionHeaderFix') commissionHeaderFixRef?: ElementRef<HTMLDivElement>;
  @ViewChild('commissionHeaderRegion') commissionHeaderRegionRef?: ElementRef<HTMLDivElement>;
  @ViewChild('commissionListBody') commissionListRef?: ElementRef<HTMLDivElement>;

  @Input() commissionList: ICommissionInfo[] = [];
  @Input() count: number = 0;

  parametersCommission!: IParametersCommission;
  queryField = new FormControl<string>('', { nonNullable: true });
  activeBankCount: number = 0;
  activePeriodCount: number = 0;
  activeProductCount: number = 0;
  banks: IBankCommission[] = [];
  periods: string[] = [];
  products: IProductCommission[] = [];
  selectedBankIds: string[] = [];
  selectedPeriods: string[] = [];
  selectedProductIds: string[] = [];
  openDropdown: 'banks' | 'periods' | 'products' | null = null;
  isFiltersExpanded: boolean = true;
  paginationResetKey: number = 0;
  isCommissionHeaderPinned: boolean = false;
  commissionHeaderHeight: number = 0;
  commissionHeaderLeft: number = 0;
  commissionHeaderWidth: number = 0;
  private readonly enablePinnedHeader: boolean = false;
  searchFilter: ISearchFilterCommission = {
    Banks: [],
    Periods: [],
    Products: [],
    Text: '',
    Skip: 0,
    Take: 10
  };

  toggleDropdown(event: Event, dropdown: 'banks' | 'periods' | 'products'): void {
    event.stopPropagation();
    this.openDropdown = this.openDropdown === dropdown ? null : dropdown;
  }

  closeDropdown(): void {
    this.openDropdown = null;
  }

  toggleFiltersVisibility(): void {
    this.isFiltersExpanded = !this.isFiltersExpanded;

    if (!this.isFiltersExpanded) {
      this.closeDropdown();
    }
  }

  toggleBank(bankId: string, checked: boolean): void {
    this.selectedBankIds = this.toggleSelection(this.selectedBankIds, bankId, checked);
  }

  togglePeriod(period: string, checked: boolean): void {
    this.selectedPeriods = this.toggleSelection(this.selectedPeriods, period, checked);
  }

  toggleProduct(productId: string, checked: boolean): void {
    this.selectedProductIds = this.toggleSelection(this.selectedProductIds, productId, checked);
  }

  isBankSelected(bankId: string): boolean {
    return this.selectedBankIds.includes(bankId.toLowerCase());
  }

  isPeriodSelected(period: string): boolean {
    return this.selectedPeriods.includes(period.toLowerCase());
  }

  isProductSelected(productId: string): boolean {
    return this.selectedProductIds.includes(productId.toLowerCase());
  }

  clearCommissionFilters(): void {
    this.selectedBankIds = [];
    this.selectedPeriods = [];
    this.selectedProductIds = [];
    this.applyCommissionFilters();
  }

  applyCommissionFilters(): void {
    const filtersToApply: IFilterContracts = {
      Context: 'invoice',
      Stages: [],
      Status: [],
      BatchStatuses: [],
      IsLegacy: false,
      IsZeroRate: false,
      WithoutBatch: false,
      ContractBatchMode: 'all',
      RateMode: 'all',
      TransferRateMode: 'all',
      StatusOfOnlending: [],
      OrderStatus: [],
      BatchStatus: true,
      StatusBank: [...this.selectedBankIds],
      StatusPeriod: [...this.selectedPeriods],
      StatusProduct: [...this.selectedProductIds]
    };

    this._dataService.applyFilters(filtersToApply);
    localStorage.setItem('bag_filter', JSON.stringify(filtersToApply));
    this.closeDropdown();
  }

  getBankTriggerText(): string {
    return this.getTriggerText(
      this.selectedBankIds,
      this.banks.map((bank) => ({ value: bank.BankId, label: bank.Name })),
      'Selecionar bancos'
    );
  }

  getPeriodTriggerText(): string {
    return this.getTriggerText(
      this.selectedPeriods,
      this.periods.map((period) => ({ value: period, label: period })),
      'Selecionar períodos'
    );
  }

  getProductTriggerText(): string {
    return this.getTriggerText(
      this.selectedProductIds,
      this.products.map((product) => ({ value: product.ProductId, label: product.Name })),
      'Selecionar produtos'
    );
  }

  listFilterSubscription!: Subscription;
  listCommissionsSubscription!: Subscription;
  firstLoad: boolean = true;
  isLoading: boolean = false;

  headerItems: IHeaderItem[] = [
    { label: 'Cod. Comissões', flexSize: 2 },
    { label: 'Empresa', flexSize: 3 },
    { label: 'Banco', flexSize: 3 },
    { label: 'Período', flexSize: 2 },
    { label: 'Resumo dos produtos', flexSize: 8, justify: 'flex-start' }
  ];

  constructor(
    private _commissionService: ApiCommissionService,
    private _dataService: DataService,
    private _dialogService: ApiDialogService
  ) { }

  ngOnInit() {
    this.getParametersCommission();
    this.loadFilterOptions();
    this.hydrateStoredFilters();
    this.listeningEvents();
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => this.updateCommissionHeaderPin());
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.updateCommissionHeaderPin();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateCommissionHeaderPin();
  }

  ngOnDestroy(): void {
    if (this.listFilterSubscription) this.listFilterSubscription.unsubscribe();
    if (this.listCommissionsSubscription)
      this.listCommissionsSubscription.unsubscribe();
  }

  getParametersCommission(): void {
    this._commissionService.getParametersCommission().subscribe({
      next: (response) => {
        if (response.Data) {
          this.parametersCommission = response.Data;

          if (!this.products.length) {
            this.products = response.Data.ProductsName || [];
          }
        }
      }
    });
  }

  loadFilterOptions(): void {
    this._commissionService.getAllStatusBankAndPeriod().subscribe({
      next: (response) => {
        const data: IFilterModalCommission | undefined = response.Data;

        this.banks = data?.Banks || [];
        this.periods = this.sortPeriodsDesc(data?.Periods || []);
        this.products = data?.Products || [];
      }
    });
  }

  getCommissionsByFilter(searchFilter: ISearchFilterCommission): void {
    if (searchFilter.Skip === 0) {
      this.isLoading = true;
    }

    this._commissionService.getAllCommissions(searchFilter).subscribe({
      next: (response) => {
        this.commissionList = searchFilter.Skip > 0
          ? [...this.commissionList, ...response.Data]
          : response.Data;
        this.count = response.Count;
        this.firstLoad = false;
        this.isLoading = false;
        this._dataService.setHeaderSummary([{ label: 'Registros', value: this.count }]);
        queueMicrotask(() => this.updateCommissionHeaderPin());
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  listeningEvents(): void {
    this.listFilterSubscription = this._dataService.listFilters.subscribe({
      next: (response) => {
        this.searchFilter.Banks = [];
        this.searchFilter.Periods = [];
        this.searchFilter.Products = [];

        if (this.firstLoad) {
          const storageFilter = localStorage.getItem('bag_filter');

          if (storageFilter != null) {
            const filters = JSON.parse(storageFilter);
            this.searchFilter.Banks = filters.StatusBank?.map((bankId: string) => { return { BankId: bankId, Name: '' }; }) || [];
            this.searchFilter.Periods.push(...((filters.StatusPeriod as string[]) || []));
            this.searchFilter.Products = filters.StatusProduct?.map((productId: string) => {
              return { ProductId: productId, Name: '' };
            }) || [];
            this.activeBankCount = filters.StatusBank?.length || 0;
            this.activePeriodCount = filters.StatusPeriod?.length || 0;
            this.activeProductCount = filters.StatusProduct?.length || 0;
            this.selectedBankIds = [...(filters.StatusBank || [])];
            this.selectedPeriods = [...(filters.StatusPeriod || [])];
            this.selectedProductIds = [...(filters.StatusProduct || [])];
          }
        } else {
          this.searchFilter.Banks = response.StatusBank?.map((bankId) => { return { BankId: bankId, Name: '' }; }) || [];
          this.searchFilter.Periods.push(...((response.StatusPeriod as string[]) || []));
          this.searchFilter.Products = response.StatusProduct?.map((productId) => {
            return { ProductId: productId, Name: '' };
          }) || [];
          this.activeBankCount = response.StatusBank?.length || 0;
          this.activePeriodCount = response.StatusPeriod?.length || 0;
          this.activeProductCount = response.StatusProduct?.length || 0;
          this.selectedBankIds = [...(response.StatusBank || [])];
          this.selectedPeriods = [...(response.StatusPeriod || [])];
          this.selectedProductIds = [...(response.StatusProduct || [])];
        }

        this.resetSkipPagination();
        this.getCommissionsByFilter(this.searchFilter);
      }
    });

    this.listCommissionsSubscription =
      this._commissionService.listCommissionsEvent.subscribe({
        next: (response) => {
          if (response) {
            this.loadFilterOptions();
            this.getCommissionsByFilter(this.searchFilter);
          }
        }
      });
  }

  searchField(text: string): void {
    let formattedString = text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '');

    this.searchFilter.Text = formattedString;

    this.resetSkipPagination();
    this.getCommissionsByFilter(this.searchFilter);
  }

  handlePagination(pagination: IPagination): void {
    this.searchFilter.Skip = pagination.Skip;
    this.searchFilter.Take = pagination.Take;

    this.getCommissionsByFilter(this.searchFilter);
  }

  resetSkipPagination(): void {
    this.searchFilter.Skip = 0;
    this.paginationResetKey += 1;
  }

  private hydrateStoredFilters(): void {
    const storageFilter = localStorage.getItem('bag_filter');

    if (!storageFilter) {
      return;
    }

    const parsedFilters = JSON.parse(storageFilter);

    this.selectedBankIds = (parsedFilters.StatusBank || []).map((bankId: string) => bankId.toLowerCase());
    this.selectedPeriods = (parsedFilters.StatusPeriod || []).map((period: string) => period.toLowerCase());
    this.selectedProductIds = (parsedFilters.StatusProduct || []).map((productId: string) => productId.toLowerCase());
  }

  private toggleSelection(values: string[], value: string, checked: boolean): string[] {
    const normalizedValue = value.toLowerCase();

    if (checked) {
      return values.includes(normalizedValue)
        ? values
        : [...values, normalizedValue];
    }

    return values.filter((currentValue) => currentValue !== normalizedValue);
  }

  private getTriggerText(
    selectedValues: string[],
    options: Array<{ value: string; label: string }>,
    fallback: string
  ): string {
    if (selectedValues.length === 0) {
      return fallback;
    }

    if (selectedValues.length === 1) {
      const selectedLabel = options.find(
        (option) => option.value.toLowerCase() === selectedValues[0]
      )?.label;

      return selectedLabel || fallback;
    }

    return `${selectedValues.length} selecionados`;
  }

  private sortPeriodsDesc(periods: string[]): string[] {
    return [...periods].sort((leftPeriod, rightPeriod) => {
      const timestampDiff =
        this.getPeriodTimestamp(rightPeriod) - this.getPeriodTimestamp(leftPeriod);

      if (timestampDiff !== 0) {
        return timestampDiff;
      }

      return rightPeriod.localeCompare(leftPeriod, 'pt-BR', {
        numeric: true,
        sensitivity: 'base'
      });
    });
  }

  private getPeriodTimestamp(period: string): number {
    const normalizedPeriod = (period || '').trim();

    const monthYearMatch = normalizedPeriod.match(/^(\d{1,2})[\/-](\d{4})$/);
    if (monthYearMatch) {
      const month = Number(monthYearMatch[1]);
      const year = Number(monthYearMatch[2]);

      if (month >= 1 && month <= 12) {
        return new Date(year, month - 1, 1).getTime();
      }
    }

    const yearMonthMatch = normalizedPeriod.match(/^(\d{4})[\/-](\d{1,2})$/);
    if (yearMonthMatch) {
      const year = Number(yearMonthMatch[1]);
      const month = Number(yearMonthMatch[2]);

      if (month >= 1 && month <= 12) {
        return new Date(year, month - 1, 1).getTime();
      }
    }

    const yearOnlyMatch = normalizedPeriod.match(/^(\d{4})$/);
    if (yearOnlyMatch) {
      const year = Number(yearOnlyMatch[1]);
      return new Date(year, 0, 1).getTime();
    }

    const fallbackTimestamp = Date.parse(normalizedPeriod);
    return Number.isNaN(fallbackTimestamp)
      ? Number.NEGATIVE_INFINITY
      : fallbackTimestamp;
  }

  openDialogCommission(): void {
    const dataDialog: IDialogCommissionData = {
      type: ActionEnum.Create,
      template: {
        title: 'Nova Comissão de Faturamento',
        parameters: this.parametersCommission,
        commissionCardId: ''
      }
    };

    this._dialogService.openDialogComission(dataDialog).then((response) => { });
  }

  private updateCommissionHeaderPin(): void {
    if (!this.enablePinnedHeader) {
      this.isCommissionHeaderPinned = false;
      this.commissionHeaderHeight = 0;
      this.commissionHeaderLeft = 0;
      this.commissionHeaderWidth = 0;
      return;
    }

    const headerElement = this.commissionHeaderFixRef?.nativeElement;
    const regionElement = this.commissionHeaderRegionRef?.nativeElement;
    const listElement = this.commissionListRef?.nativeElement;

    if (!headerElement || !regionElement || !listElement) {
      return;
    }

    const regionRect = regionElement.getBoundingClientRect();
    const listRect = listElement.getBoundingClientRect();
    const headerHeight = headerElement.offsetHeight;
    const topOffset = this.getModernHeaderOffset();
    const shouldPin = regionRect.top <= topOffset && listRect.bottom > topOffset + headerHeight + 12;

    this.commissionHeaderHeight = headerHeight;
    this.commissionHeaderLeft = listRect.left;
    this.commissionHeaderWidth = listRect.width;
    this.isCommissionHeaderPinned = shouldPin;
  }

  private getModernHeaderOffset(): number {
    const rootStyles = getComputedStyle(document.documentElement);
    const desktopOffset = parseFloat(rootStyles.getPropertyValue('--ui-modern-header-height'));
    const mobileOffset = parseFloat(rootStyles.getPropertyValue('--ui-modern-header-height-mobile'));
    const isMobile = window.matchMedia('(max-width: 720px)').matches;

    if (isMobile) {
      return Number.isFinite(mobileOffset) ? mobileOffset : 64;
    }

    return Number.isFinite(desktopOffset) ? desktopOffset : 76;
  }
}
